import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import useAntdResizableHeader from 'use-antd-resizable-header'
import { useVT } from 'virtualizedtableforantd4'

import { LntTable } from '@app/components/base'
import { Subheader } from '@app/components'

import { SIZE } from '@app/constants'
import { useElementSize } from '@app/hooks/useElementSize'

import styles from './styles.module.scss'

import { rowClassName, getEllipsisColumns, getTableScrollY } from './utils'
import { defaultPagination } from './configs'

import type { Column } from '@app/components/base/Table/types'
import type { HTMLAttributes, TdHTMLAttributes, WheelEventHandler } from 'react'
import type { BaseDataSourceType, ExtendedTableFC, ExtendedTableProps } from './types'

export const ExtendedTable: ExtendedTableFC = <DataSourceType extends BaseDataSourceType>({
  className: outerClassName,
  columns: rawColumns,
  size = SIZE.middle,
  bordered = true,
  showSorterTooltip = false,
  control,
  toolbar,
  pagination,
  onRowDoubleClick,
  onWheelUp,
  onWheelDown,
  ...props
}: ExtendedTableProps<DataSourceType>) => {
  const className = classNames(styles.extendedTable, outerClassName)

  const { height: wrapperHeight, ref: tableWrapperRef } = useElementSize<HTMLDivElement>()

  const scrollY = useMemo(
    () => getTableScrollY(tableWrapperRef.current, wrapperHeight),
    [tableWrapperRef, wrapperHeight]
  )

  const handleRow = (
    record: DataSourceType,
    rowIndex?: number
  ): HTMLAttributes<unknown> | TdHTMLAttributes<unknown> => ({
    onDoubleClick: event => onRowDoubleClick?.(event, record, rowIndex)
  })

  const columns = useMemo<Array<Column<DataSourceType>> | undefined>(getEllipsisColumns(rawColumns, size), [
    rawColumns,
    size
  ])

  const { components, resizableColumns } = useAntdResizableHeader({
    columns,
    maxConstraints: 1750
  })

  const handleWheel: WheelEventHandler<HTMLDivElement> = (event): void => {
    const { deltaY } = event

    if (deltaY < 0) {
      return onWheelUp?.(deltaY)
    }

    if (deltaY > 0) {
      return onWheelDown?.(deltaY)
    }
  }

  const [vt, setComponents] = useVT(() => ({ scroll: { y: scrollY }, overscanRowCount: 15 }), [scrollY])

  useEffect(() => {
    setComponents(components)
  }, [columns])

  return (
    <section className={ className }>
      <Subheader
        className={ styles.extendedTable__subheader }
        control={ control }
        toolbar={ toolbar }
      />

      <div
        ref={ tableWrapperRef }
        className={ styles.extendedTable__tableWrapper }
        onWheel={ handleWheel }
      >
        <LntTable<DataSourceType>
          { ...props }
          components={ vt }
          columns={ resizableColumns }
          size={ size }
          bordered={ bordered }
          showSorterTooltip={ showSorterTooltip }
          rowClassName={ rowClassName }
          pagination={ { ...defaultPagination, ...pagination } }
          onRow={ handleRow }
          scroll={ { x: 'max-content', y: scrollY } }
        />
      </div>
    </section>
  )
}
